import { Modal } from "antd";
import React, { FC, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import FormDatePicker from "../../form-components/DatePicker";
import { Alert, Button, Col, Row } from "reactstrap";
import { LaborFlagOptions, WagesType } from "./RateCardCreation";
import { ReactTyped } from "react-typed";
import { getDirtyFieldsOnly } from "../../../utils/use-form/GetDirtyFieldsOnly";
const RateCardWageModal: FC<{
  open: boolean;
  title: "Add New Labor" | "Edit Labor";
  mode: "create" | "edit" | "view";
  onCancel: () => void;
  append: (data: any) => void;
  update: (index: any, dirtyData: any, data: any) => void;
  extra?: {
    index?: number;
    defaultData?: any;
    sourceData?: any;
    currencyOptions?: any;
    laborTypeOptions?: any;
    laborCategoryOptions?: any;
    RateCardPaymentTypeOptions?: any;
  };
}> = ({ open, title, mode, onCancel, append, update, extra }) => {
  const [warningWage, setWarningWage] = useState<{
    index: number | undefined;
    message: string | undefined;
  }>({
    index: undefined,
    message: undefined,
  });
  const methods = useForm<WagesType>({
    mode: "onChange",
    defaultValues: {
      ...extra?.defaultData,
    },
  });

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  const resetWarningWage = () => {
    setWarningWage({
      index: undefined,
      message: undefined,
    });
  };

  const validateWages = (source: WagesType[], target: WagesType) => {
    try {
      const { laborFlag, laborTypeId, laborCategoryId, paymentType } = target;
      for (let i = 0; i < source?.length; i++) {
        {
          const wage = source[i];
          if (
            wage.laborFlag === laborFlag &&
            wage.laborTypeId === laborTypeId &&
            wage.laborCategoryId === laborCategoryId &&
            wage.paymentType === paymentType
          ) {
            setWarningWage({
              index: source.length,
              message: "An identical labor wage entry already exists.",
            });
            return false;
          }
        }
      }
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  };

  return (
    <Modal
      maskClosable={false}
      title={title}
      animation={false}
      open={open}
      footer={null}
      width={"90%"}
      onCancel={() => onCancel()}
    >
      <FormProvider {...methods}>
        <form>
          <div className={` mb-3 p-2 `}>
            <div className="w-100">
              <Row className="w-100 mt-1">
                <Col lg="3">
                  <FormSelect
                    name={`laborFlag`}
                    label="Labor Flag"
                    options={LaborFlagOptions}
                    required={true}
                    onChange={(_, option) => {
                      resetWarningWage();
                    }}
                  />
                </Col>

                <Col lg="3">
                  <FormSelect
                    name={`laborTypeId`}
                    label="Labor Type"
                    options={extra?.laborTypeOptions}
                    required={true}
                    onChange={(_, option) => {
                      setValue("laborTypeName", option.label);
                      resetWarningWage();
                    }}
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name={`laborCategoryId`}
                    label="Labor Category"
                    options={extra?.laborCategoryOptions}
                    onChange={(_, option) => {
                      setValue("laborCategoryName", option.label);
                      resetWarningWage();
                    }}
                    required={true}
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name={`currencyId`}
                    label="Currency"
                    options={extra?.currencyOptions?.map((item: any) => ({
                      value: item.id,
                      label: item.currency_code,
                    }))}
                    onChange={(_, option) => {
                      setValue("currencyCode", option.label);
                    }}
                    required={true}
                  />
                </Col>
              </Row>
              <Row className="w-100 mt-2">
                <Col lg="3">
                  <FormSelect
                    name={`paymentType`}
                    label="Payment Type"
                    options={extra?.RateCardPaymentTypeOptions}
                    required={true}
                    disabled={title === "Edit Labor" && mode !== "create"}
                    onChange={(_, option) => {
                      resetWarningWage();
                    }}
                  />
                </Col>
                {watch(`paymentType`) === "Daily" ? (
                  <>
                    <Col lg="3">
                      <FormInput
                        name={`dayRate`}
                        label="Day Rate"
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                      />
                    </Col>
                  </>
                ) : watch(`paymentType`) === "Hourly" ? (
                  <>
                    <Col lg="3">
                      <FormInput
                        name={`baseHourlyRate`}
                        label="Base Hourly Rate (Input in Rate/Hr)"
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                      />
                    </Col>
                    <Col lg="3">
                      <FormInput
                        name={`overTimeRate`}
                        label="Over Time Rate (Input in Rate/Hr)"
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                      />
                    </Col>
                    <Col lg="3">
                      <FormInput
                        name={`weekendRate`}
                        label="Weekend Rate (Input in Rate/Hr)"
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                      />
                    </Col>
                  </>
                ) : (
                  <>
                    <Col lg="3">
                      <FormInput
                        name={`monthlyRate`}
                        label="Monthly Rate"
                        type="number"
                        pattern={/^\d+(\.\d+)?$/}
                        required={true}
                        min={1}
                      />
                    </Col>
                    <Col lg="3">
                      <FormDatePicker
                        name={`paymentDate`}
                        label="Payment Date"
                        required={true}
                      />
                    </Col>
                  </>
                )}
              </Row>
              <Row className="w-100 mt-2">
                {watch(`paymentType`) === "Hourly" ? (
                  <Col lg="3">
                    <FormInput
                      name={`nightShiftRate`}
                      label="Night Shift Rate (Input in Rate/Hr)"
                      type="number"
                      pattern={/^\d+(\.\d+)?$/}
                      required={true}
                      min={1}
                    />
                  </Col>
                ) : (
                  <></>
                )}
              </Row>
            </div>
            <Alert
              isOpen={warningWage?.index !== undefined}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <ReactTyped
                strings={[warningWage?.message || ""]}
                typeSpeed={15}
                showCursor={false}
              />
            </Alert>
          </div>
          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                {title === "Edit Labor" ? (
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      onClick={handleSubmit((data) => {
                        const fields = extra?.sourceData;
                        const isValid =
                          fields?.length > 0
                            ? validateWages(
                                fields?.filter(
                                  (_: any, idx: any) => idx != extra?.index
                                ),
                                data
                              )
                            : true;
                        if (!isValid) return;

                        const dirtyData = getDirtyFieldsOnly(data, dirtyFields);
                        if (
                          dirtyData === undefined ||
                          Object.keys(dirtyData).length === 0
                        ) {
                          onCancel();
                          return;
                        }
                        update(extra?.index, dirtyData, data); // pass only updated fields
                        onCancel();
                      })}
                    >
                      Update Labor
                    </Button>
                  </li>
                ) : (
                  <li>
                    <Button
                      className="btn btn-primary btn-sm"
                      type="button"
                      color="primary"
                      onClick={handleSubmit((data) => {
                        const fields = extra?.sourceData;
                        const isValid =
                          fields?.length > 0
                            ? validateWages(fields, data)
                            : true;
                        if (!isValid) return;
                        append(data);
                        onCancel();
                      })}
                    >
                      Add Labor
                    </Button>
                  </li>
                )}
                <li>
                  <a
                    href="#cancel"
                    onClick={(ev) => {
                      ev.preventDefault();
                      onCancel();
                    }}
                    className="link link-light"
                  >
                    Cancel
                  </a>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default RateCardWageModal;
